import { createContext, useContext, type ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { House, ICON_STROKE } from './icons';

/**
 * Переход на стартовый экран. Контекстом, а не пропсом каждого экрана: кнопка нужна везде,
 * и протаскивать колбэк через два десятка экранов ради одной иконки — шум.
 * `null` — кнопку не показывать (мы уже на стартовом экране).
 */
export const HomeContext = createContext<(() => void) | null>(null);

export function HomeButton(): JSX.Element | null {
  const goHome = useContext(HomeContext);
  const { t } = useI18n();
  if (!goHome) return null;
  return (
    <button
      type="button"
      aria-label={t('home')}
      title={t('home')}
      onClick={goHome}
      className="-my-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-2 text-ink-2 transition active:bg-surface"
    >
      <House size={18} strokeWidth={ICON_STROKE} />
    </button>
  );
}

/**
 * Каркас экрана Mini App: шапка, прокручиваемое тело, закреплённые снизу элементы.
 *
 * Прокручивается ТОЛЬКО тело. Прокрутка страницы целиком внутри Telegram означает, что
 * жест «потянуть вниз» закрывает приложение, а шапка и нижняя навигация уезжают из виду
 * посреди выбора слота.
 *
 * Общий для клиента и кабинета: у обоих та же шапка и та же геометрия, различается
 * только нижняя навигация (ТЗ §14.3).
 */
export function Screen(props: { children: ReactNode }): JSX.Element {
  // `relative` — опора для закреплённых снизу CtaBar, TabBar и шторок: без неё они
  // позиционировались бы относительно окна и уезжали бы за пределы Mini App.
  return <div className="relative flex h-full flex-col bg-bg text-ink">{props.children}</div>;
}

export function TopBar(props: {
  title: string;
  subtitle?: string | undefined;
  onBack?: (() => void) | undefined;
  right?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-hair bg-bg px-4 pb-3 pt-[calc(env(safe-area-inset-top,0px)+14px)]">
      {props.onBack ? (
        <button
          type="button"
          aria-label={props.title}
          onClick={props.onBack}
          className="-my-1 text-[26px] leading-none text-accent"
        >
          ‹
        </button>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[16px] font-bold tracking-tight">{props.title}</div>
        {props.subtitle ? (
          <div className="truncate text-[12px] text-ink-3">{props.subtitle}</div>
        ) : null}
      </div>
      {props.right}
      <HomeButton />
    </div>
  );
}

/**
 * Прокручиваемая часть экрана.
 *
 * `bottomGap` — запас под закреплённую CTA и нижнюю навигацию. Без него последняя карточка
 * списка навсегда прячется под кнопкой: докрутить до неё нельзя, и выглядит это как
 * потерянная запись.
 */
export function ScrollArea(props: {
  children: ReactNode;
  bottomGap?: 'none' | 'cta' | 'tabs' | 'both';
}): JSX.Element {
  const gap = {
    none: 'pb-4',
    cta: 'pb-[calc(env(safe-area-inset-bottom,0px)+96px)]',
    tabs: 'pb-[calc(env(safe-area-inset-bottom,0px)+84px)]',
    both: 'pb-[calc(env(safe-area-inset-bottom,0px)+168px)]',
  }[props.bottomGap ?? 'none'];

  return <div className={`no-scrollbar min-h-0 flex-1 overflow-y-auto ${gap}`}>{props.children}</div>;
}

/** Заголовок секции — прототипный `.psec-t`. */
export function SectionTitle(props: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div
      className={`mb-2.5 text-[12px] font-bold uppercase tracking-[0.07em] text-ink-3 ${props.className ?? ''}`}
    >
      {props.children}
    </div>
  );
}

export function Section(props: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={`px-4 pt-5 ${props.className ?? ''}`}>{props.children}</div>;
}
